import Cookies from 'js-cookie';

const isProduction = process.env.REACT_APP_PRODUCTION === 'true';
export const TOKEN_KEY = isProduction ? 'FINANCEIRO_TOKEN' : 'FINANCEIRO_TOKEN_TEST';

// Mesmo `domain` que o app sempre usou no Cookies.set — sem ele o Cookies.remove não apaga o legado.
// Sem `domain` quando a env não existe (ambiente de teste/jsdom).
const COOKIE_DOMAIN = process.env.REACT_APP_COOKIE_DOMAIN;
const COOKIE_OPTS = COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : {};

// localStorage LANÇA (não devolve null) em aba anônima do Safari, cota cheia ou storage bloqueado.
const safe = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem(key, value); return true; } catch { return false; } },
  remove: (key) => { try { localStorage.removeItem(key); } catch { /* sem storage, nada a remover */ } },
};

/** Ordem: storage → cookie legado (promove e apaga) → null. Nunca cookie primeiro. */
export function getToken() {
  const stored = safe.get(TOKEN_KEY);
  if (stored) return stored;

  const legacy = Cookies.get(TOKEN_KEY);
  if (legacy) {
    safe.set(TOKEN_KEY, legacy); // sessão de antes do deploy: leva para o storage…
    Cookies.remove(TOKEN_KEY, COOKIE_OPTS); // …e apaga o cookie, sem deslogar
    return legacy;
  }

  return null;
}

/** Login OK. Se o storage aceitou, não deixa cookie; se falhou, cai no comportamento antigo (cookie). */
export function setToken(token) {
  const ok = safe.set(TOKEN_KEY, token);
  if (ok) Cookies.remove(TOKEN_KEY, COOKIE_OPTS);
  else Cookies.set(TOKEN_KEY, token, COOKIE_OPTS);
}

/** Logout, mount da tela de login, 401 tratado centralmente. Limpa os dois lugares. */
export function removeToken() {
  safe.remove(TOKEN_KEY);
  Cookies.remove(TOKEN_KEY, COOKIE_OPTS);
}

/**
 * Dados de sessão que este app gravava em cookie no domínio `.cargozilla.com.br`.
 * go2route, jornada, diesel e ciot gravam ESTAS MESMAS chaves no MESMO domínio — cookie é
 * identificado por nome + domínio, então era um cookie só, compartilhado: trocar a empresa no
 * header de um app trocava a do outro, e TENANT_PAINEL alimenta o header `tenant` que faz o
 * filtro multi-tenant no backend. No localStorage cada origem tem o seu, o que encerra
 * a colisão para este app.
 */
export const SESSION_KEYS = {
  USERNAME: 'USERNAME',
  FULLNAME: 'USUARIO_PAINEL',
  COMPANIES: 'COMPANIES_PAINEL',
  SELECTED_COMPANIES: 'SELECTED_COMPANIES',
  TENANT: 'TENANT_PAINEL',
};

/**
 * Ordem: storage → cookie legado (promove) → null.
 * Ao contrário do token, NÃO apaga o cookie: essas chaves ainda são a sessão de go2route,
 * jornada, diesel e ciot no mesmo domínio — apagar aqui zeraria a empresa selecionada deles.
 * O cookie fica órfão para este app, que a partir daqui só lê e escreve no storage.
 */
function promote(key) {
  const stored = safe.get(key);
  if (stored !== null) return stored;

  const legacy = Cookies.get(key);
  if (legacy === undefined) return null;

  safe.set(key, legacy);
  return legacy;
}

/** Valor de texto (USERNAME, USUARIO_PAINEL, TENANT_PAINEL). */
export function getSessionItem(key) {
  return promote(key);
}

/** Valor JSON (COMPANIES_PAINEL, SELECTED_COMPANIES). Valor corrompido vale como ausente. */
export function getSessionJSON(key) {
  const raw = promote(key);
  if (raw === null) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setSessionItem(key, value) {
  const ok = safe.set(key, value);
  // Só quando o storage recusa: sem isso o app fica logado (token no cookie de fallback) e sem
  // empresa/tenant, e o Header não renderiza.
  if (!ok) Cookies.set(key, value, COOKIE_OPTS);
}

export function setSessionJSON(key, value) {
  const ok = safe.set(key, JSON.stringify(value));
  // js-cookie serializa o objeto sozinho — mesmo formato que o storage guarda.
  if (!ok) Cookies.set(key, value, COOKIE_OPTS);
}

/**
 * Preferências de tela, que sofriam da mesma colisão: go2route, jornada e diesel gravam
 * PESSOA_SEARCH e SIDEBAR_EXPANDED com o mesmo nome no mesmo domínio. E este app guarda a
 * pessoa selecionada inteira em PESSOA_SEARCH — era dado de pessoa cruzando módulos, não só
 * preferência de tela.
 * Fora a colisão, cookie sobe em TODA requisição para o domínio; nada disso o servidor lê.
 */
export const PREF_KEYS = {
  SIDEBAR_EXPANDED: 'SIDEBAR_EXPANDED',
  PESSOA_SEARCH: 'PESSOA_SEARCH',
  SELECTED_COMPANY: 'SELECTED_COMPANY',
};

export function removeSessionItem(key) {
  safe.remove(key);
}

/** Logout. Só o storage deste app — os cookies de mesmo nome pertencem aos outros módulos. */
export function clearSession() {
  Object.values(SESSION_KEYS).forEach((key) => safe.remove(key));
}
